import { useIsAssistantGenerating } from "#features/conversations/hooks/useIsAssistantGenerating.ts";
import { isObject, readString } from "#features/conversations/lib/untypedFields.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { dispatchCustomEvent } from "#lib/events/eventDispatcher.ts";
import type { PreparedActionStatus } from "#types/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

// "approved": the member approved the call and its result is not saved yet, so the draft is still open.
const OPEN_STATUSES: readonly PreparedActionStatus[] = ["pending", "approved"];
const CONFIRM_POLL_MS = 3_000;
const CONFIRM_TIMEOUT_MS = 60_000;

// The approved calls whose result is not saved yet: a drafted action by its message id, a call approved in the chat by
// its toolCallId, so each card settles on its own.
const awaitingOutcome = ({ id, preparedAction, toolApprovals }: ConversationMessageModel): string[] => [
    ...(preparedAction?.status === "approved" ? [id] : []),
    ...(toolApprovals ?? []).flatMap(({ calls }) => calls.flatMap(({ toolCallId, decision, outcome }) => (decision === "allow" && !outcome ? [toolCallId] : [])))
];

/**
 * A prepared action's state and an approved call's outcome live in the conversation's replay events, which only a
 * fetch returns. When a turn ends while a draft is still open, the conversation refetches. While a call is approved
 * and its result is not saved yet, it refetches every few seconds for up to a minute; past that, the call's key from
 * awaitingOutcome is in the returned set. When a draft's status turns sent or failed, the host hears it once. A discard tells the host
 * nothing.
 */
export const usePreparedActionSync = (conversationId: string, messages: ConversationMessageModel[]): ReadonlySet<string> => {
    const queryClient = useQueryClient();
    const isGenerating = useIsAssistantGenerating(conversationId);
    const wasGenerating = useRef(isGenerating);
    const statuses = useRef<Map<string, PreparedActionStatus> | null>(null);
    const approvedSince = useRef(new Map<string, number>());
    const [unsettled, setUnsettled] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        const hasOpenDraft = messages.some(message => (message.preparedAction && OPEN_STATUSES.includes(message.preparedAction.status)) || awaitingOutcome(message).length > 0);
        if (wasGenerating.current && !isGenerating && hasOpenDraft) {
            queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
        }
        wasGenerating.current = isGenerating;
    }, [conversationId, isGenerating, messages, queryClient]);

    // Keyed on the ids, not on the messages array: a refetch that still reads approved keeps the same array (structural
    // sharing), so the interval, not a new render, drives the next refetch.
    const approvedIds = messages.flatMap(awaitingOutcome).join(",");
    useEffect(() => {
        if (isGenerating || approvedIds === "") {
            return;
        }
        const approved = approvedIds.split(",");
        const start = Date.now();
        approved.forEach(id => {
            if (!approvedSince.current.has(id)) {
                approvedSince.current.set(id, start);
            }
        });
        const poll = setInterval(() => {
            const now = Date.now();
            const overdue = approved.filter(id => now - approvedSince.current.get(id)! >= CONFIRM_TIMEOUT_MS);
            setUnsettled(current => (overdue.every(id => current.has(id)) ? current : new Set([...current, ...overdue])));
            if (overdue.length === approved.length) {
                clearInterval(poll);

                return;
            }
            queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
        }, CONFIRM_POLL_MS);

        return () => clearInterval(poll);
    }, [approvedIds, conversationId, isGenerating, queryClient]);

    useEffect(() => {
        const previous = statuses.current;
        statuses.current = new Map(messages.flatMap(message => (message.preparedAction ? [[message.id, message.preparedAction.status] as const] : [])));
        if (previous === null) {
            return;
        }

        messages.forEach(({ id, preparedAction }) => {
            const before = previous.get(id);
            if (!preparedAction || before === undefined || !OPEN_STATUSES.includes(before)) {
                return;
            }
            if (preparedAction.status === "sent" || preparedAction.status === "failed") {
                const action = isObject(preparedAction.action) ? preparedAction.action : {};
                dispatchCustomEvent("waiPreparedActionSent", {
                    conversationId,
                    messageId: id,
                    kind: readString(action, "kind") ?? "",
                    source: preparedAction.source ?? null,
                    status: preparedAction.status
                });
            }
        });
    }, [conversationId, messages]);

    return unsettled;
};
